'use client';

import { type ReactNode } from 'react';

import { useSelectedLayoutSegment } from 'next/navigation';

import { cn } from '@ps/ui/utils';

/** The child route segment under `[slug]` that opens the thread pane. */
const THREAD_SEGMENT = 'thread';

interface Props {
  /** The persistent channel pane, rendered by the `[slug]` layout. */
  channel: ReactNode;
  /** The active `[slug]` child route — the thread pane, or nothing (index). */
  children: ReactNode;
}

/**
 * Two-pane shell *inside* the channel detail — channel messages on the left, the
 * thread on the right. The thread is a NESTED CHILD ROUTE of `[slug]`
 * (`…/[slug]/thread/[messageId]`), passed in as `children`; the channel lives in
 * the layout so it stays mounted while the thread route swaps.
 *
 * "Thread open" is read from {@link useSelectedLayoutSegment} — the active child
 * segment of the `[slug]` layout — NOT `usePathname()`. This is deliberate: when
 * a soft-nav intercept opens over the page (e.g. the `/checkout` paywall modal
 * launched from a thread message), the live pathname becomes `/checkout`, but
 * the preserved background route's selected segment stays `thread`, so the thread
 * pane remains visible behind the overlay instead of collapsing.
 *
 * Avoid a stray base `lg:flex` on the thread column: Tailwind emits responsive
 * utilities *after* unprefixed ones, so `lg:flex` would beat `hidden` at `lg` and
 * an empty column would still claim `flex-1`, narrowing the channel.
 *
 * On mobile: only one pane shows — the channel when no thread, the thread when a
 * thread segment is active (the channel is hidden, the thread takes `flex-1`).
 */
export function ChannelDetailThreadPanes({ channel, children }: Props) {
  const segment = useSelectedLayoutSegment();
  const isThreadOpen = segment === THREAD_SEGMENT;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div
        className={cn(
          'min-h-0 min-w-0 flex-1 flex-col',
          isThreadOpen ? 'hidden lg:flex' : 'flex',
        )}
      >
        {channel}
      </div>
      {isThreadOpen ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:max-w-[460px]">
          {children}
        </div>
      ) : null}
    </div>
  );
}
